import { useCallback, useRef } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DropResult } from 'react-beautiful-dnd';
import type { Subscription } from 'rxjs/Subscription';
import { useFlagsService } from '@atlassian/jira-flags';
import usePortalGroupsResource from '@atlassian/jira-servicedesk-request-types-config-common/src/controllers/portal-groups/index.tsx';
import { REORDER_GROUP } from '../../common/ui/flags/constants.tsx';
import getErrorFlag from '../../common/ui/flags/main.tsx';
import { useActionsProvider } from '../../services/store';
import useTracking from '../tracking';

const useReorderGroups = () => {
	const { reorderGroups } = useActionsProvider();
	const { fireError, fireTrack } = useTracking();
	const { showFlag } = useFlagsService();
	const pendingSubscriptionRef = useRef<Subscription>();
	const { clear: clearPortalGroupsResourceCache } = usePortalGroupsResource();

	return useCallback(
		(result: DropResult) => {
			const { source, destination } = result;
			if (source && destination && source.index !== destination.index) {
				if (pendingSubscriptionRef.current) {
					pendingSubscriptionRef.current.unsubscribe();
				}
				pendingSubscriptionRef.current = reorderGroups(
					source.index,
					destination.index,
					(_, error) => {
						if (error) {
							showFlag(getErrorFlag(REORDER_GROUP, error));
							fireError('reorderGroups', error);
						} else {
							fireTrack('portalGroups reordered');
							clearPortalGroupsResourceCache();
						}
					},
				);
			}
		},
		[clearPortalGroupsResourceCache, fireError, fireTrack, reorderGroups, showFlag],
	);
};

export default useReorderGroups;
